import {
  APPLICATION_STAGES,
  BROWSE_STAGE_LABELS,
  BROWSE_STAGES,
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABELS,
  EXPERIENCE_BANDS,
  overBudgetLabel,
  type ApplicationStage,
  type BrowseStage,
  type CandidateBrowseRow,
} from '@nest-recruit/shared';
import type { Tone } from '../../../../components/Tone';
import { EMPTY, formatIsoDate, formatNumber } from '../../../../lib/format';
import { salaryLabel } from '../../candidate-format';
import { activeFilterCount, listOf, withFilter, withList, type CandidateBrowseSearch, type FilterKey, type ListKey } from './browse-address';

/** The tiles' and the stage chips' tones (mockup Main.dc.html), in the app's tone set (ADR 0035). */
export const BROWSE_STAGE_TONES: Readonly<Record<BrowseStage, Tone>> = {
  ranking: 'new',
  review: 'screening',
  shortlist: 'progress',
  contact: 'attention',
  assessment: 'assessment',
  interview: 'interview',
  selected: 'done',
  rejected: 'rejected',
};

/** "2 live · 3 in total" under the job a row shows. */
export function applicationsText(live: number, total: number): string {
  return `${formatNumber(live)} live · ${formatNumber(total)} in total`;
}

/** "Accounts Officer · Dhaka", or null when neither is known. */
export function titleAndCity(row: CandidateBrowseRow): string | null {
  const parts = [row.candidate.currentTitle, row.candidate.city].filter((part): part is string => part !== null && part.trim() !== '');
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * The over-budget chip's hover text. The row carries the ask and the percentage, not the
 * budget's top, so the top is named by how far over it is rather than as an amount.
 */
export function overBudgetTitle(row: CandidateBrowseRow): string | null {
  const percent = row.candidate.expectedSalaryOverBudgetPercent;
  if (percent === null) return null;
  const asks = salaryLabel(row.candidate.expectedSalaryMinor, row.candidate.expectedSalaryCurrency);
  const over = overBudgetLabel(percent).toLowerCase();
  return asks === null ? `Asks ${over} for this job` : `Asks ${asks}, ${over} for this job`;
}

// --- The removable chips above the list -----------------------------------------------------------

export interface FilterChip {
  readonly id: string;
  readonly label: string;
  /** The address once this chip is removed. */
  readonly without: CandidateBrowseSearch;
}

/** Names for ids and keys the address holds; a missing one shows as a dash, never as an id. */
export interface ChipLookups {
  readonly jobs: ReadonlyMap<string, string>;
  readonly tags: ReadonlyMap<string, string>;
  readonly sources: ReadonlyMap<string, string>;
  readonly pools: ReadonlyMap<string, string>;
  readonly customFields: ReadonlyMap<string, string>;
  readonly stageLabels: Readonly<Record<ApplicationStage, string>>;
}

const LIST_CHIP_PREFIX: Readonly<Record<ListKey, string>> = {
  stages: 'Stage',
  jobs: 'Job',
  tags: 'Tag',
  skills: 'Skill',
  experience: 'Experience',
  cities: 'City',
  reached: 'Reached',
  sources: 'Source',
  pools: 'Pool',
};

function isOneOf<T extends string>(options: readonly T[], value: string): value is T {
  return options.some((option) => option === value);
}

function listValueLabel(key: ListKey, value: string, lookups: ChipLookups): string {
  if (key === 'stages') return isOneOf(BROWSE_STAGES, value) ? BROWSE_STAGE_LABELS[value] : EMPTY;
  if (key === 'jobs') return lookups.jobs.get(value) ?? EMPTY;
  if (key === 'tags') return lookups.tags.get(value) ?? EMPTY;
  if (key === 'sources') return lookups.sources.get(value) ?? value;
  if (key === 'pools') return lookups.pools.get(value) ?? EMPTY;
  if (key === 'experience') return EXPERIENCE_BANDS.find((band) => band.value === value)?.label ?? EMPTY;
  if (key === 'reached') return isOneOf(APPLICATION_STAGES, value) ? lookups.stageLabels[value] : EMPTY;
  return value;
}

function taka(value: string): string {
  return `৳${formatNumber(Number(value))}`;
}

/** "From – to" as one phrase: "৳40,000–৳60,000", "from ৳40,000", "up to ৳60,000". */
function rangeText(from: string | undefined, to: string | undefined, format: (value: string) => string): string {
  if (from !== undefined && to !== undefined) return `${format(from)}–${format(to)}`;
  if (from !== undefined) return `from ${format(from)}`;
  return `up to ${format(to ?? '')}`;
}

function without(search: CandidateBrowseSearch, keys: readonly FilterKey[]): CandidateBrowseSearch {
  return keys.reduce((next, key) => withFilter(next, key, undefined), search);
}

/** One chip per active filter value, in the filter panel's order. */
export function filterChips(search: CandidateBrowseSearch, lookups: ChipLookups): readonly FilterChip[] {
  if (activeFilterCount(search) === 0) return [];
  const chips: FilterChip[] = [];
  const list = (key: ListKey): void => {
    const values = listOf(search, key);
    for (const value of values) {
      chips.push({
        id: `${key}:${value}`,
        label: `${LIST_CHIP_PREFIX[key]}: ${listValueLabel(key, value, lookups)}`,
        without: withList(search, key, values.filter((item) => item !== value)),
      });
    }
  };
  if (search.text) chips.push({ id: 'text', label: `Search: “${search.text}”`, without: without(search, ['text']) });
  list('stages');
  list('jobs');
  if (search.starred === 'me') chips.push({ id: 'starred', label: 'Starred by me', without: without(search, ['starred']) });
  list('tags');
  const skillsWithout = listOf(search, 'skills').length === 1 ? without(search, ['skills', 'skillMode']) : null;
  for (const value of listOf(search, 'skills')) {
    const rest = listOf(search, 'skills').filter((item) => item !== value);
    chips.push({ id: `skills:${value}`, label: `Skill: ${value}`, without: skillsWithout ?? withList(search, 'skills', rest) });
  }
  list('experience');
  if (search.education && isOneOf(EDUCATION_LEVELS, search.education)) {
    chips.push({ id: 'education', label: `Education: at least ${EDUCATION_LEVEL_LABELS[search.education]}`, without: without(search, ['education']) });
  }
  list('cities');
  if (search.salaryMin !== undefined || search.salaryMax !== undefined) {
    chips.push({ id: 'salary', label: `Expects ${rangeText(search.salaryMin, search.salaryMax, taka)} a month`, without: without(search, ['salaryMin', 'salaryMax']) });
  }
  if (search.overBudget === 'yes') chips.push({ id: 'overBudget', label: 'Over budget', without: without(search, ['overBudget']) });
  if (search.maxNoticeDays !== undefined) {
    chips.push({ id: 'notice', label: `Notice: at most ${search.maxNoticeDays} days`, without: without(search, ['maxNoticeDays']) });
  }
  list('reached');
  list('sources');
  if (search.from !== undefined || search.to !== undefined) {
    chips.push({ id: 'applied', label: `Applied ${rangeText(search.from, search.to, formatIsoDate)}`, without: without(search, ['from', 'to']) });
  }
  if (search.addedFrom !== undefined || search.addedTo !== undefined) {
    chips.push({ id: 'added', label: `Added ${rangeText(search.addedFrom, search.addedTo, formatIsoDate)}`, without: without(search, ['addedFrom', 'addedTo']) });
  }
  list('pools');
  if (search.pool) chips.push({ id: 'pool', label: search.pool === 'any_pool' ? 'In a pool' : 'In no pool', without: without(search, ['pool']) });
  if (search.customField !== undefined) {
    const name = lookups.customFields.get(search.customField) ?? EMPTY;
    const label = search.customValue === undefined ? name : `${name} contains “${search.customValue}”`;
    chips.push({ id: 'custom', label, without: without(search, ['customField', 'customValue']) });
  }
  return chips;
}
